import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Ban,
  CheckCircle,
  ExternalLink,
  FileText,
  HelpCircle,
  ImageOff,
  Loader2,
  RotateCcw,
  ShieldCheck,
  Undo2,
  XCircle,
  Scale,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { migrationV1V2Api } from '../api/migration-v1-v2';
import { RecordStatusBadge } from './RecordStatusBadge';
import { formatBytes, recordStatusOptions } from './lotPresentation';
import type { FileVerdict, MigrationFileEntry, MigrationFilesResponse, RecordStatus } from '../types';

const PAGE_SIZE = 12;

/**
 * Relances menées de front lors d'une relance de page. Chacune re-télécharge
 * un fichier de la V1 et le ré-envoie à la V2 : au-delà de quelques appels
 * simultanés on sature les deux plateformes pour rien.
 */
const RETRY_CONCURRENCY = 3;

const VERDICT_META: Record<FileVerdict, { label: string; className: string; Icon: typeof CheckCircle }> = {
  ok: { label: 'Vérifié — identique', className: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300', Icon: CheckCircle },
  size_mismatch: { label: 'Tailles différentes', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', Icon: Scale },
  missing_v1: { label: 'Absent côté V1', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', Icon: XCircle },
  v1_unreachable: { label: 'V1 injoignable', className: 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300', Icon: HelpCircle },
  missing_v2: { label: 'Absent côté V2', className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', Icon: XCircle },
  v2_unreachable: { label: 'Stockage V2 injoignable', className: 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300', Icon: HelpCircle },
  not_migrated: { label: 'Non migré', className: 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300', Icon: HelpCircle },
  unverifiable: { label: 'Non vérifiable côté V2', className: 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300', Icon: HelpCircle },
};

/**
 * Aperçu du fichier V2 d'un record. Les fichiers migrés sont PRIVATE côté
 * V2 : pas d'URL directe — on passe par le proxy ADM (authentifié via
 * l'apiClient) et un object URL local.
 */
function V2FilePreview({ entry }: { entry: MigrationFileEntry }) {
  // Logo PUBLIC : URL directe, pas de proxy nécessaire.
  const directUrl = entry.v2.url;
  const needsProxy = !directUrl && entry.v2.proxyAvailable && entry.status === 'migrated';

  const [proxyUrl, setProxyUrl] = useState<string | null>(null);
  const [proxyState, setProxyState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    if (!needsProxy) return;
    let cancelled = false;
    let url: string | null = null;

    migrationV1V2Api
      .fetchRecordV2FileObjectUrl(entry.recordId)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        url = u;
        setProxyUrl(u);
        setProxyState('ready');
      })
      .catch(() => {
        if (!cancelled) setProxyState('error');
      });

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [entry.recordId, needsProxy]);

  if (needsProxy && proxyState === 'loading') {
    return <Skeleton className="h-24 w-full rounded-md" />;
  }

  const objectUrl = directUrl ?? (proxyState === 'ready' ? proxyUrl : null);
  if (!objectUrl) {
    return (
      <div className="h-24 w-full rounded-md border border-dashed flex flex-col items-center justify-center gap-1 text-gray-400 text-xs">
        <ImageOff className="h-5 w-5" />
        {needsProxy ? 'Aperçu V2 indisponible' : entry.status !== 'migrated' ? 'Non migré' : 'Pas d’aperçu V2'}
      </div>
    );
  }

  if (entry.kind === 'image') {
    return (
      <a href={objectUrl} target="_blank" rel="noreferrer" title="Ouvrir en taille réelle">
        <img
          src={objectUrl}
          alt={`Aperçu V2 — ${entry.name}`}
          className="h-24 w-full rounded-md object-contain bg-gray-50 dark:bg-slate-900 border"
        />
      </a>
    );
  }

  return (
    <a
      href={objectUrl}
      target="_blank"
      rel="noreferrer"
      className="h-24 w-full rounded-md border flex flex-col items-center justify-center gap-1 text-gray-500 hover:bg-gray-50 dark:hover:bg-slate-800 text-xs"
    >
      <FileText className="h-6 w-6" />
      Ouvrir le fichier V2
    </a>
  );
}

function V1FilePreview({ entry }: { entry: MigrationFileEntry }) {
  const [failed, setFailed] = useState(false);

  if (!entry.v1.url || (entry.v1.checked && entry.v1.ok === false)) {
    const httpCode = entry.v1.httpCode ?? 0;
    const label = !entry.v1.url
      ? 'Pas de fichier V1'
      : httpCode >= 400 && httpCode < 500
      ? 'Absent côté V1'
      : 'V1 injoignable';
    return (
      <div className="h-24 w-full rounded-md border border-dashed flex flex-col items-center justify-center gap-1 text-gray-400 text-xs">
        <ImageOff className="h-5 w-5" />
        {label}
      </div>
    );
  }

  if (entry.kind === 'image' && !failed) {
    return (
      <a href={entry.v1.url} target="_blank" rel="noreferrer" title="Ouvrir en taille réelle">
        <img
          src={entry.v1.url}
          alt={`Aperçu V1 — ${entry.name}`}
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-24 w-full rounded-md object-contain bg-gray-50 dark:bg-slate-900 border"
        />
      </a>
    );
  }

  return (
    <a
      href={entry.v1.url}
      target="_blank"
      rel="noreferrer"
      className="h-24 w-full rounded-md border flex flex-col items-center justify-center gap-1 text-gray-500 hover:bg-gray-50 dark:hover:bg-slate-800 text-xs"
    >
      {failed ? <ImageOff className="h-6 w-6" /> : <FileText className="h-6 w-6" />}
      Ouvrir le fichier V1
    </a>
  );
}

/**
 * Onglet « Fichiers » du visualiseur de lot : chaque fichier migré est montré
 * des DEUX côtés (V1 à gauche, V2 à droite) pour contrôler visuellement la
 * migration, avec une vérification serveur à la demande (présence + taille).
 */
export function StepFilesPanel({
  stepExecutionId,
  statusFilter = '',
  onStatusFilterChange,
  onCountersChanged,
}: {
  stepExecutionId: number;
  /** Filtre statut (partagé avec l'onglet Données) — filtré côté serveur, requête indexée. */
  statusFilter?: RecordStatus | '';
  onStatusFilterChange?: (status: RecordStatus | '') => void;
  /** Notifie la page hôte qu'une action a changé les compteurs du lot (exclusion, relance…). */
  onCountersChanged?: () => void;
}) {
  const { hasPermission } = useAuth();
  const canExecute = hasPermission('action.migration_v1_v2.execute');
  const [response, setResponse] = useState<MigrationFilesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  // recordId en cours de relance unitaire ; `-1` = relance de toute la page.
  const [retrying, setRetrying] = useState<number | null>(null);
  // Exclusion manuelle (raison obligatoire) + ré-inclusion.
  const [excludeTarget, setExcludeTarget] = useState<MigrationFileEntry | null>(null);
  const [excludeReason, setExcludeReason] = useState('');
  const [excluding, setExcluding] = useState(false);
  const [excludeError, setExcludeError] = useState<string | null>(null);
  const [including, setIncluding] = useState<number | null>(null);
  // Avancement et bilan de la relance de page (cf. handleRetryPage).
  const [retryProgress, setRetryProgress] = useState<{ done: number; total: number } | null>(null);
  const [retryOutcome, setRetryOutcome] = useState<{
    ok: number;
    failed: number;
    firstError: string | null;
  } | null>(null);

  const fetchFiles = useCallback(
    async (verify: boolean) => {
      try {
        if (verify) {
          setVerifying(true);
        } else {
          setLoading(true);
        }
        setError('');
        const res = await migrationV1V2Api.getStepFiles(stepExecutionId, {
          page,
          limit: PAGE_SIZE,
          status: statusFilter,
          verify,
        });
        setResponse(res);
      } catch {
        setError('Impossible de charger les fichiers du lot');
      } finally {
        setLoading(false);
        setVerifying(false);
      }
    },
    [stepExecutionId, page, statusFilter]
  );

  useEffect(() => {
    fetchFiles(false);
  }, [fetchFiles]);

  // Retour page 1 quand le filtre change (select ci-dessous ou clic sur une
  // carte de stats de la page hôte).
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  // Le bilan porte sur UNE page : on l'efface dès qu'on change de page ou de
  // filtre, sinon il reste affiché au-dessus de fichiers qu'il ne décrit plus.
  useEffect(() => {
    setRetryOutcome(null);
  }, [page, statusFilter]);

  // Relance unitaire d'un fichier : rejoue le loader (quel que soit le statut
  // du record) → re-télécharge V1 + ré-upload V2. Utile pour re-pousser une
  // photo déjà migrée après un correctif de loader (ex. securityLevel).
  const handleRetry = useCallback(
    async (recordId: number) => {
      try {
        setRetrying(recordId);
        await migrationV1V2Api.retryRecord(recordId);
      } catch {
        // silencieux — le statut/verdict rafraîchi reflète l'échec éventuel.
      } finally {
        setRetrying(null);
        await fetchFiles(false);
        onCountersChanged?.();
      }
    },
    [fetchFiles, onCountersChanged]
  );

  // Exclusion manuelle : même mécanique que l'onglet Données — le record ne
  // compte plus en échec (il est « traité »), la raison est reprise dans le
  // rapport, et il reste ré-incluable à tout moment.
  const handleExcludeConfirm = useCallback(async () => {
    if (!excludeTarget || excludeReason.trim() === '') return;
    try {
      setExcluding(true);
      setExcludeError(null);
      await migrationV1V2Api.excludeRecord(excludeTarget.recordId, excludeReason.trim());
      setExcludeTarget(null);
      setExcludeReason('');
      await fetchFiles(false);
      onCountersChanged?.();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setExcludeError(err.response?.data?.error ?? err.message ?? String(e));
    } finally {
      setExcluding(false);
    }
  }, [excludeTarget, excludeReason, fetchFiles, onCountersChanged]);

  const handleInclude = useCallback(
    async (recordId: number) => {
      try {
        setIncluding(recordId);
        await migrationV1V2Api.includeRecord(recordId);
        await fetchFiles(false);
        onCountersChanged?.();
      } catch {
        // silencieux — le statut rafraîchi reflète l'état réel.
      } finally {
        setIncluding(null);
      }
    },
    [fetchFiles, onCountersChanged]
  );

  /**
   * Relance de tous les fichiers de la page courante.
   *
   * Chaque relance est synchrone côté serveur (re-téléchargement V1 +
   * ré-upload V2, quelques secondes par fichier). L'ancienne version les
   * enchaînait une par une en avalant chaque erreur : sur une page de fichiers
   * en échec, elle tournait plusieurs minutes puis se terminait sans rien
   * afficher — « rien ne se passe » du point de vue de l'opérateur. On borne
   * donc la concurrence, on affiche l'avancement, et on rend compte du
   * résultat.
   *
   * ⚠️ Une relance peut réussir en HTTP tout en laissant le record en échec
   * (le loader capture l'erreur côté serveur et répond 200) : le bilan se lit
   * sur le `status` renvoyé, jamais sur le seul succès de l'appel.
   */
  const handleRetryPage = useCallback(async () => {
    const entries = response?.data ?? [];
    if (entries.length === 0) return;

    const queue = [...entries];
    let ok = 0;
    let failed = 0;
    let firstError: string | null = null;
    let done = 0;

    const worker = async () => {
      for (;;) {
        const entry = queue.shift();
        if (!entry) return;
        try {
          const record = await migrationV1V2Api.retryRecord(entry.recordId);
          if (record.status === 'migrated' || record.status === 'excluded') {
            ok++;
          } else {
            failed++;
            firstError ??= record.errorMessage ?? 'Échec sans message';
          }
        } catch (e: unknown) {
          failed++;
          const err = e as { response?: { data?: { error?: string } }; message?: string };
          firstError ??= err.response?.data?.error ?? err.message ?? String(e);
        } finally {
          done++;
          setRetryProgress({ done, total: entries.length });
        }
      }
    };

    try {
      setRetryOutcome(null);
      setRetryProgress({ done: 0, total: entries.length });
      setRetrying(-1);
      await Promise.all(Array.from({ length: RETRY_CONCURRENCY }, worker));
    } finally {
      setRetryOutcome({ ok, failed, firstError });
      setRetryProgress(null);
      setRetrying(null);
      await fetchFiles(false);
      onCountersChanged?.();
    }
  }, [response, fetchFiles, onCountersChanged]);

  if (loading && !response) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-48 rounded-xl" />
        ))}
      </div>
    );
  }

  if (error) {
    return <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>;
  }

  if (!response || !response.supported) {
    return <p className="text-sm text-gray-400 italic">Ce lot ne comporte pas de fichiers.</p>;
  }

  const { data, meta, summary, verified } = response;

  return (
    <div className="space-y-4">
      {/* Barre d'actions : vérification + bilan */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {onStatusFilterChange && (
            <Select
              value={statusFilter || 'all'}
              onValueChange={(val) =>
                onStatusFilterChange((val === 'all' ? '' : val) as RecordStatus | '')
              }
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                {recordStatusOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchFiles(true)}
            disabled={verifying || retrying !== null}
          >
            {verifying ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ShieldCheck className="h-3.5 w-3.5" />
            )}
            Vérifier cette page
          </Button>
          {canExecute && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetryPage}
              disabled={verifying || retrying !== null}
              title="Rejoue le loader sur tous les fichiers de cette page (re-télécharge V1 + ré-upload V2)"
            >
              {retrying === -1 ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5" />
              )}
              {retryProgress
                ? `Re-migration… ${retryProgress.done}/${retryProgress.total}`
                : 'Re-migrer cette page'}
            </Button>
          )}
          {verified && summary && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {(Object.entries(summary) as [FileVerdict, number][]).map(([verdict, count]) => {
                const meta_ = VERDICT_META[verdict];
                if (!meta_ || !count) return null;
                return (
                  <span
                    key={verdict}
                    className={cn('inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium', meta_.className)}
                  >
                    <meta_.Icon className="h-3 w-3" />
                    {count} {meta_.label.toLowerCase()}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <span className="text-xs text-gray-500">
          {meta.total} fichier{meta.total > 1 ? 's' : ''} — page {meta.page}/{Math.max(1, meta.totalPages)}
        </span>
      </div>

      {/* Bilan de la dernière relance de page — sans lui, une relance dont
          tous les fichiers échouent se termine sans le moindre retour. */}
      {retryOutcome && (
        <div
          className={cn(
            'rounded-lg border p-3 text-sm',
            retryOutcome.failed === 0
              ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300'
              : 'bg-orange-50 border-orange-200 text-orange-800 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-300'
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="font-medium">
                Re-migration terminée : {retryOutcome.ok} réussi{retryOutcome.ok > 1 ? 's' : ''},{' '}
                {retryOutcome.failed} en échec
              </p>
              {retryOutcome.firstError && (
                <p className="text-xs opacity-90">Première erreur : {retryOutcome.firstError}</p>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setRetryOutcome(null)}>
              Masquer
            </Button>
          </div>
        </div>
      )}

      {/* Grille de comparaison V1 / V2 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.map((entry) => {
          const verdictMeta = entry.verdict ? VERDICT_META[entry.verdict] : null;
          return (
            <div
              key={entry.recordId}
              className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 shadow-sm p-3 space-y-2"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate" title={entry.name}>
                  {entry.name}
                </p>
                <RecordStatusBadge status={entry.status} />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">V1</p>
                  <V1FilePreview entry={entry} />
                  <p className="text-[11px] text-gray-500">
                    {entry.v1.checked ? formatBytes(entry.v1.size) : ' '}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">V2</p>
                  <V2FilePreview entry={entry} />
                  <p className="text-[11px] text-gray-500">
                    {entry.v2.checked
                      ? formatBytes(entry.v2.size)
                      : entry.declaredSize
                      ? formatBytes(entry.declaredSize)
                      : ' '}
                  </p>
                </div>
              </div>

              {verdictMeta && (
                <span className={cn('inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium', verdictMeta.className)}>
                  <verdictMeta.Icon className="h-3 w-3" />
                  {verdictMeta.label}
                </span>
              )}

              {entry.errorMessage && (
                <p className="text-xs text-red-600 line-clamp-2" title={entry.errorMessage}>
                  {entry.errorMessage}
                </p>
              )}

              <div className="flex items-center justify-between gap-2 pt-0.5">
                {entry.v1.url ? (
                  <a
                    href={entry.v1.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Fichier V1 d'origine
                  </a>
                ) : (
                  <span />
                )}
                <div className="flex items-center gap-1">
                  {canExecute && entry.status !== 'migrated' && entry.status !== 'excluded' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-[11px] text-gray-500 hover:text-gray-700"
                      onClick={() => {
                        setExcludeError(null);
                        setExcludeReason('');
                        setExcludeTarget(entry);
                      }}
                      disabled={retrying !== null || excluding}
                      title="Exclure ce fichier de la migration (raison obligatoire, reprise dans le rapport)"
                    >
                      <Ban className="h-3.5 w-3.5" />
                      Exclure
                    </Button>
                  )}
                  {canExecute && entry.status === 'excluded' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-[11px] text-blue-600 hover:text-blue-700"
                      onClick={() => handleInclude(entry.recordId)}
                      disabled={retrying !== null || including !== null}
                      title="Ré-inclure ce fichier dans la migration"
                    >
                      {including === entry.recordId ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Undo2 className="h-3.5 w-3.5" />
                      )}
                      Ré-inclure
                    </Button>
                  )}
                  {canExecute && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-[11px] text-orange-600 hover:text-orange-700"
                      onClick={() => handleRetry(entry.recordId)}
                      disabled={retrying !== null}
                      title="Rejoue le loader sur ce fichier (re-télécharge V1 + ré-upload V2)"
                    >
                      {retrying === entry.recordId ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RotateCcw className="h-3.5 w-3.5" />
                      )}
                      Re-migrer
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {data.length === 0 && (
        <p className="text-sm text-gray-400 italic text-center py-8">
          {statusFilter ? 'Aucun fichier avec ce statut.' : 'Aucun fichier sur cette page.'}
        </p>
      )}

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
            Précédent
          </Button>
          <span className="text-sm text-gray-500">
            {meta.page} / {meta.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= meta.totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Suivant
          </Button>
        </div>
      )}

      <p className="text-xs text-gray-400">
        La vérification contrôle, pour chaque fichier de la page, sa présence et sa taille des deux
        côtés (V1 public et stockage V2). « Vérifié — identique » = le fichier V2 est strictement
        la même copie que le fichier V1.
      </p>

      {/* Dialog d'exclusion manuelle : même mécanique que l'onglet Données —
          raison obligatoire, reprise telle quelle dans le rapport de migration. */}
      <Dialog
        open={!!excludeTarget}
        onOpenChange={(open) => {
          if (!open && !excluding) setExcludeTarget(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="h-4 w-4 text-gray-500" />
              Exclure de la migration
            </DialogTitle>
            <DialogDescription>
              {excludeTarget ? `« ${excludeTarget.name} »` : 'Ce fichier'} ne sera pas repris en
              V2. Il restera visible ici avec sa raison, sera listé dans le rapport de migration,
              ne comptera plus comme un échec du lot, et pourra être ré-inclus à tout moment.
            </DialogDescription>
          </DialogHeader>
          {excludeError && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
              {excludeError}
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Raison de l'exclusion <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={excludeReason}
              onChange={(e) => setExcludeReason(e.target.value)}
              placeholder="Ex. : photo supprimée côté V1 (fichier introuvable), personne partie de l'école."
              rows={3}
            />
            <p className="text-xs text-gray-500">
              Cette raison sera reprise telle quelle dans le rapport de migration — rédigez-la
              pour un lecteur non technique.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExcludeTarget(null)} disabled={excluding}>
              Annuler
            </Button>
            <Button
              onClick={handleExcludeConfirm}
              disabled={excluding || excludeReason.trim() === ''}
            >
              {excluding ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Ban className="h-4 w-4" />
              )}
              Exclure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
